import { type ReactElement, useMemo, useState } from 'react';
import { type AnalyticsBiQueryView } from '@vello/api-client';
import { type ResultRow } from '@vello/analytics-semantic';
import { 
  CheckRow,
  ColumnChart,
  ComboChart,
  DonutChart,
  HeatmapGrid,
  LineChart,
  Notice,
  Panel,
  ParetoChart,
  SegmentedControl,
  Select,
  StackedAreaChart,
  StackedBarChart,
  type ChartExportLabels,
  type ChartSelection, Stack, Row } from '@vello/web-theme';
import {
  type BiUnit,
  type Category,
  type CurrencyGroup,
  type ExplorerChartKind,
  type LabelOf,
  type PlotValue,
  categoriesOf,
  chartKinds,
  comboValid,
  currencyGroups,
  matrixOf,
  seriesByMetric,
  singleMeasure,
  stackOf,
  supportsPercent,
  isTimeAxis,
  orientation,
} from '../../../src/index';
import { TrendCharts, type BiText } from './bi-ui';

/**
 * The Explorer's chart (D529): a picker of the chart TYPES that are valid for the shape of the answer (n measures × up to two cuts —
 * see `chartKinds`), the measure picker when a type plots one measure of several, the 100 % option for stacks, and one chart per
 * currency (money never shares an axis). Every chart carries its text summary, a PNG and a CSV export, and reports a click on a mark
 * through `onSelect({ dimension, value })` — the contract the cross-filter builds on. The result TABLE stays where it is: this
 * component draws only the chart, so the filter work around the Explorer does not collide with it.
 */
export interface ExplorerChartProps {
  readonly view: AnalyticsBiQueryView;
  readonly text: BiText;
  /** A mark was chosen (a bar, a column, a cell…); wired to the cross-filter by the screen. */
  readonly onSelect?: (selection: ChartSelection) => void;
  /** The values currently chosen per dimension, so the other marks recede. */
  readonly selected?: Readonly<Record<string, readonly string[]>>;
}

const KIND_ORDER: readonly ExplorerChartKind[] = ['line', 'columns', 'bars', 'donut', 'pareto', 'grouped', 'stacked-columns', 'stacked-bars', 'stacked-area', 'heatmap', 'combo'];

export function ExplorerChart({ view, text, onSelect, selected }: ExplorerChartProps): ReactElement | null {
  const { t } = text;
  const dimIds = view.dimensions.map((d) => d.id);
  const kinds = useMemo(() => chartKinds({ metrics: view.metrics, dimensions: view.dimensions, rowCount: view.rows.length }), [view.metrics, view.dimensions, view.rows.length]);
  const [picked, setPicked] = useState<ExplorerChartKind | undefined>(undefined);
  const [pickedMetric, setPickedMetric] = useState<string | undefined>(undefined);
  const [percent, setPercent] = useState(false);
  const kind = picked !== undefined && kinds.includes(picked) ? picked : kinds[0];
  if (kind === undefined) return null;

  const metricIds = view.metrics.map((m) => m.id);
  const oneMeasure = singleMeasure(kind, dimIds.length);
  const metricId = pickedMetric !== undefined && metricIds.includes(pickedMetric) ? pickedMetric : (metricIds[0] as string);
  const unitOf = (id: string): BiUnit => (view.metrics.find((m) => m.id === id)?.unit ?? 'count') as BiUnit;
  const label: LabelOf = (dim, key, l) => text.valueText(dim, key, l, isTimeAxis(dim));
  const exportLabels: ChartExportLabels = { menu: t('bi.chart.export'), png: t('bi.chart.exportPng'), csv: t('bi.chart.exportCsv') };
  const groups = currencyGroups(view.rows);
  const pickable = kinds.length > 1 || (oneMeasure && metricIds.length > 1) || supportsPercent(kind);

  return (
    <Panel>
      <Stack gap={4}>
        {pickable && (
          <Row gap={3} align="center" wrap>
            {kinds.length > 1 && (
              <div className="vello-admin-a-grow-wide">
                <SegmentedControl ariaLabel={t('bi.chart.type')} value={kind} options={[...kinds].sort((a, b) => KIND_ORDER.indexOf(a) - KIND_ORDER.indexOf(b)).map((k) => ({ id: k, label: t(`bi.chart.kind.${k}`) }))} onChange={setPicked} />
              </div>
            )}
            {oneMeasure && metricIds.length > 1 && (
              <Select id="bi-chart-metric" aria-label={t('bi.chart.metric')} value={metricId} onChange={(e) => setPickedMetric(e.target.value)}>
                {metricIds.map((id) => (
                  <option key={id} value={id}>
                    {text.metricName(id)}
                  </option>
                ))}
              </Select>
            )}
            {supportsPercent(kind) && (
              <CheckRow id="bi-chart-percent" checked={percent} onChange={setPercent}>
                {t('bi.chart.percent')}
              </CheckRow>
            )}
          </Row>
        )}
        <Stack gap={5}>
          {groups.map((g) => (
            <GroupChart key={g.currency ?? 'none'} group={g} showCurrency={groups.length > 1} kind={kind} view={view} text={text} metricId={metricId} metricIds={metricIds} unitOf={unitOf} label={label} exportLabels={exportLabels} percent={percent && supportsPercent(kind)} {...(onSelect !== undefined ? { onSelect } : {})} {...(selected !== undefined ? { selected } : {})} />
          ))}
        </Stack>
      </Stack>
    </Panel>
  );
}

interface GroupProps {
  readonly group: CurrencyGroup;
  readonly showCurrency: boolean;
  readonly kind: ExplorerChartKind;
  readonly view: AnalyticsBiQueryView;
  readonly text: BiText;
  readonly metricId: string;
  readonly metricIds: readonly string[];
  readonly unitOf: (id: string) => BiUnit;
  readonly label: LabelOf;
  readonly exportLabels: ChartExportLabels;
  readonly percent: boolean;
  readonly onSelect?: (selection: ChartSelection) => void;
  readonly selected?: Readonly<Record<string, readonly string[]>>;
}

/** One chart of one currency group. */
function GroupChart(props: GroupProps): ReactElement | null {
  const { group, kind, view, text, metricId, metricIds, unitOf, label, exportLabels, percent } = props;
  const { t, v, fmt } = text;
  const dims = view.dimensions.map((d) => d.id);
  const currency = group.currency;
  const rows = group.rows;
  const plot: PlotValue = (id, r: ResultRow) => {
    const cell = r.values[id];
    return cell === undefined || cell.value === null ? null : fmt.chartValue(unitOf(id), cell, r.currency);
  };
  const metricName = (id: string): string => text.metricName(id);
  const format = (id: string) => (n: number): string => fmt.chartText(unitOf(id), n, currency);
  const suffix = currency !== undefined && props.showCurrency ? ` · ${currency}` : '';
  const dimTitle = dims.map((d) => text.dimName(d)).join(' × ');
  const common = { emptyLabel: t('bi.chart.empty'), exportLabels };
  // The values chosen on a dimension (the active cross-filter), in the words the chart reports a click with: a chart that reports the
  // category it drew (its label) is handed the labels of the chosen keys, so its chosen marks carry `data-chosen`. Pareto and the heat
  // grid report the key itself and take the keys as they are (`cats` left out).
  const select = (dim: string, cats?: readonly Category[]): { dimension: string; selected: readonly string[]; onSelect?: (s: ChartSelection) => void } => {
    const keys = props.selected?.[dim] ?? [];
    return {
      dimension: dim,
      selected: cats === undefined ? keys : cats.filter((c) => keys.includes(c.key)).map((c) => c.label),
      ...(props.onSelect !== undefined ? { onSelect: props.onSelect } : {}),
    };
  };
  const note = (shown: number, cut: number): ReactElement | null => (cut > 0 ? <Notice tone="neutral">{t('bi.chart.truncated', { shown, total: shown + cut })}</Notice> : null);

  // ── one cut ───────────────────────────────────────────────────────────────────────────────────────────────────
  if (dims.length === 1) {
    const dim = dims[0] as string;
    const cats = categoriesOf(rows, dim, label);
    const capped = cats.slice(0, 40);
    const ids = kind === 'combo' || kind === 'grouped' || kind === 'line' || kind === 'stacked-area' || kind === 'stacked-bars' ? [...metricIds] : [metricId];
    const series = seriesByMetric(rows, dim, capped, ids.map((id) => ({ id, name: metricName(id) })), plot);
    const title = `${ids.length === 1 ? metricName(ids[0] as string) : dimTitle}${ids.length === 1 ? ` · ${text.dimName(dim)}` : ''}${suffix}`;
    const labels = capped.map((c) => c.label);
    const fmtFirst = format(ids[0] as string);

    if (kind === 'line' && ids.length === 1 && isTimeAxis(dim)) {
      return <TrendCharts view={{ ...view, rows }} grain={dim as 'year' | 'month' | 'day' | 'hour'} metric={metricId} title={`${metricName(metricId)}${suffix}`} text={text} loading={false} />;
    }
    if (kind === 'line') {
      return <LineChart title={title} labels={labels} series={series.map((s) => ({ key: s.key, label: s.label, values: s.values }))} formatValue={fmtFirst} height={240} {...common} {...select(dim, capped)} />;
    }
    if (kind === 'columns') {
      return (
        <Stack gap={2}>
          <ColumnChart title={title} categories={labels} series={[{ key: metricId, label: metricName(metricId), values: series[0]?.values ?? [] }]} formatValue={fmtFirst} height={240} {...common} {...select(dim, capped)} />
          {note(capped.length, cats.length - capped.length)}
        </Stack>
      );
    }
    if (kind === 'bars') {
      return (
        <Stack gap={2}>
          <StackedBarChart title={title} categories={labels} series={[{ key: metricId, label: metricName(metricId), values: series[0]?.values ?? [] }]} formatValue={fmtFirst} {...common} {...select(dim, capped)} />
          {note(capped.length, cats.length - capped.length)}
        </Stack>
      );
    }
    if (kind === 'donut') {
      const pick = select(dim);
      return (
        <DonutChart
          title={title}
          slices={capped.map((c, i) => ({ key: c.key, label: c.label, value: series[0]?.values[i] ?? 0 }))}
          formatValue={fmtFirst}
          formatShare={(f) => fmt.percent(f)}
          emptyLabel={t('bi.chart.empty')}
          size={180}
          {...(pick.onSelect !== undefined ? { onSelect: (slice: { key?: string; label: string }) => pick.onSelect?.({ dimension: dim, value: slice.key ?? slice.label }) } : {})}
          selectedKeys={pick.selected}
        />
      );
    }
    if (kind === 'pareto') {
      return (
        <ParetoChart
          title={title}
          items={capped.map((c, i) => ({ key: c.key, label: c.label, value: series[0]?.values[i] ?? null }))}
          formatValue={fmtFirst}
          formatShare={(f) => fmt.percent(f)}
          classLabel={(c) => v('analytics.pareto_class', c)}
          cumulativeLabel={t('bi.chart.cumulative')}
          {...common}
          {...select(dim)}
        />
      );
    }
    if (kind === 'grouped') {
      return <ComboChart title={title} categories={labels} bars={series} lines={[]} formatValue={fmtFirst} {...common} {...select(dim, capped)} />;
    }
    if (kind === 'stacked-bars') {
      return <StackedBarChart title={title} categories={labels} series={series} percent={percent} formatValue={fmtFirst} formatShare={(f) => fmt.percent(f)} totalLabel={t('bi.chart.total')} {...common} {...select(dim, capped)} />;
    }
    if (kind === 'stacked-area') {
      return <StackedAreaChart title={title} labels={labels} series={series} percent={percent} formatValue={fmtFirst} formatShare={(f) => fmt.percent(f)} totalLabel={t('bi.chart.total')} {...common} {...select(dim, capped)} />;
    }
    if (kind === 'combo' && comboValid(view.metrics)) {
      const [barSeries, ...lineSeries] = series;
      const rightId = ids[1] as string;
      return (
        <ComboChart
          title={`${dimTitle}${suffix}`}
          categories={labels}
          bars={barSeries !== undefined ? [barSeries] : []}
          lines={lineSeries}
          leftAxisLabel={metricName(ids[0] as string)}
          rightAxisLabel={lineSeries.length === 1 ? metricName(rightId) : ''}
          formatValue={fmtFirst}
          formatRight={format(rightId)}
          {...common}
          {...select(dim, capped)}
        />
      );
    }
    return null;
  }

  // ── two cuts: one measure over a matrix ───────────────────────────────────────────────────────────────────────
  if (dims.length === 2) {
    const pair = [dims[0], dims[1]] as [string, string];
    const title = `${metricName(metricId)} · ${dimTitle}${suffix}`;
    const f = format(metricId);
    if (kind === 'heatmap') {
      const mx = matrixOf(rows, pair, metricId, label, plot);
      const [rowDim, colDim] = orientation(pair);
      return (
        <Stack gap={2}>
          <HeatmapGrid
            title={title}
            rowLabels={mx.rows.map((c) => c.label)}
            colLabels={mx.cols.map((c) => c.label)}
            rowKeys={mx.rows.map((c) => c.key)}
            colKeys={mx.cols.map((c) => c.key)}
            values={mx.cells}
            rowDimension={rowDim}
            colDimension={colDim}
            formatValue={f}
            summaryWords={{ highest: t('bi.chart.highest'), lowest: t('bi.chart.lowest'), empty: t('bi.chart.empty') }}
            {...common}
            {...(props.onSelect !== undefined ? { onSelect: props.onSelect } : {})}
            selected={[...(props.selected?.[rowDim] ?? []), ...(props.selected?.[colDim] ?? [])]}
          />
          {note(mx.rows.length + mx.cols.length, mx.truncated.rows + mx.truncated.cols)}
        </Stack>
      );
    }
    const st = stackOf(rows, pair, metricId, label, plot);
    const labels = st.categories.map((c) => c.label);
    const series = st.series.map((s) => ({ key: s.key, label: s.label, values: s.values }));
    const cut = note(st.categories.length + st.series.length, st.truncated.categories + st.truncated.series);
    const body =
      kind === 'stacked-bars' ? (
        <StackedBarChart title={title} categories={labels} series={series} percent={percent} formatValue={f} formatShare={(x) => fmt.percent(x)} totalLabel={t('bi.chart.total')} {...common} {...select(st.categoryDimension, st.categories)} />
      ) : kind === 'stacked-area' ? (
        <StackedAreaChart title={title} labels={labels} series={series} percent={percent} formatValue={f} formatShare={(x) => fmt.percent(x)} totalLabel={t('bi.chart.total')} {...common} {...select(st.categoryDimension, st.categories)} />
      ) : kind === 'grouped' ? (
        <ComboChart title={title} categories={labels} bars={series} lines={[]} formatValue={f} {...common} {...select(st.categoryDimension, st.categories)} />
      ) : (
        <ColumnChart title={title} categories={labels} series={series} percent={percent} formatValue={f} formatShare={(x) => fmt.percent(x)} totalLabel={t('bi.chart.total')} height={260} {...common} {...select(st.categoryDimension, st.categories)} />
      );
    return (
      <Stack gap={2}>
        {body}
        {cut}
      </Stack>
    );
  }
  return null;
}
